<template>
  <base-view id="proposal" class="bg-gray-900">
    <div class="max-w-2xl mx-auto bg-gray-900 section pt-12">
      <h1 class="mb-8 text-4xl font-bold">Proposals</h1>
      <div class="flex flex-col items-center">
        <div class="max-w-md px-12">
          <img src="@/assets/RMRGreen_Logo_White.png" />
        </div>
        <div class="bg-gray-900 text-white p-8 max-w-3xl mx-auto rounded-lg">
          <h2 v-if="!formSubmitted" class="text-2xl font-bold text-center">
            Ready to simplify your benefits administration?
          </h2>
          <div v-if="formSubmitted" class="text-center my-24">
            <h2 class="text-2xl font-bold">Thank You!</h2>
            <p class="mt-2 text-xl">
              Your form has been submitted successfully. We will get back to you
              soon.
            </p>
          </div>
          <div v-else>
            <ul class="list-disc ml-12 my-4 text-base">
              <li>Modern Technology</li>
              <li>Unparalleled Client Support & Account Management</li>
              <li>No More Junk Fees Charged to Your Employees</li>
            </ul>
            <p class="text-center font-semibold text-md">
              Fill out the information below to schedule a demo or request a
              proposal
            </p>

            <form
              ref="contactForm"
              @submit.prevent="sendEmail"
              class="mt-6 space-y-4"
            >
              <!-- Contact Information -->
              <p class="border-b border-white w-full font-semibold">
                Contact Information
              </p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block">First Name</label>
                  <input
                    type="text"
                    v-model="form.firstName"
                    placeholder="First Name"
                    class="focus:outline-none w-full p-2 bg-gray-725 rounded placeholder:text-gray-400"
                    required
                  />
                </div>
                <div>
                  <label class="block">Last Name</label>
                  <input
                    type="text"
                    v-model="form.lastName"
                    placeholder="Last Name"
                    class="w-full p-2 bg-gray-725 rounded placeholder:text-gray-400"
                    required
                  />
                </div>
              </div>
              <div>
                <label class="block">Work Email</label>
                <input
                  type="email"
                  v-model="form.email"
                  placeholder="Work Email"
                  class="w-full p-2 bg-gray-725 rounded"
                  required
                />
              </div>

              <!-- Company Information -->
              <p class="border-b border-white w-full font-semibold">
                Company Information
              </p>
              <div>
                <input
                  type="text"
                  v-model="form.companyName"
                  placeholder="Company Name"
                  class="w-full p-2 bg-gray-725 rounded placeholder:text-gray-400"
                  required
                />
              </div>
              <div>
                <input
                  type="text"
                  v-model="form.headquartersState"
                  placeholder="Headquarters State"
                  class="w-full p-2 bg-gray-725 rounded placeholder:text-gray-400"
                  required
                />
              </div>
              <div>
                <input
                  type="text"
                  v-model="form.website"
                  placeholder="Website"
                  class="w-full p-2 bg-gray-725 rounded placeholder:text-gray-400"
                  required
                />
                <p
                  v-if="errors.website != ''"
                  class="text-red-500 text-sm mt-2 ml-4"
                >
                  * {{ errors.website }}
                </p>
              </div>
              <div>
                <input
                  type="number"
                  v-model="form.employees"
                  placeholder="# of Benefit Eligible Employees"
                  class="w-full p-2 bg-gray-725 rounded placeholder:text-gray-400"
                  required
                />
              </div>

              <!-- Benefits Information -->
              <p class="border-b border-white w-full font-semibold">
                Benefits Information
              </p>
              <div
                class="flex flex-col sm:flex-row justify-center items-center"
              >
                <label class="block text-left w-full sm:w-1/3"
                  >Desired Start Date</label
                >
                <input
                  type="date"
                  v-model="form.startDate"
                  class="w-full sm:w-2/3 p-2 bg-gray-800 border border-gray-700 rounded"
                  required
                />
              </div>
              <fieldset>
                <legend class="block font-semibold">
                  Services Interested In:
                </legend>
                <div class="space-y-2 ml-4 sm:ml-12">
                  <label class="flex items-center"
                    ><input
                      type="checkbox"
                      v-model="form.services"
                      value="FSA"
                      class="mr-4 w-5 h-5"
                    />
                    FSA - Flexible Spending Accounts</label
                  >
                  <label class="flex items-center"
                    ><input
                      type="checkbox"
                      v-model="form.services"
                      value="HSA"
                      class="mr-4 w-5 h-5"
                    />
                    HSA - Health Savings Accounts</label
                  >
                  <label class="flex items-center"
                    ><input
                      type="checkbox"
                      v-model="form.services"
                      value="HRA"
                      class="mr-4 w-5 h-5"
                    />
                    HRA - Health Reimbursement Accounts</label
                  >
                  <label class="flex items-center"
                    ><input
                      type="checkbox"
                      v-model="form.services"
                      value="LSA"
                      class="mr-4 w-5 h-5"
                    />
                    LSA - Lifestyle Spending Accounts</label
                  >
                  <label class="flex items-center"
                    ><input
                      type="checkbox"
                      v-model="form.services"
                      value="Cobra"
                      class="mr-4 w-5 h-5"
                    />
                    COBRA</label
                  >
                  <label class="flex items-center"
                    ><input
                      type="checkbox"
                      v-model="form.services"
                      value="Direct Billing"
                      class="mr-4 w-5 h-5"
                    />
                    Direct Billing / Retiree Billing</label
                  >
                </div>
              </fieldset>
              <p
                v-if="errors.services != ''"
                class="text-red-500 text-sm mt-2 ml-4"
              >
                * {{ errors.services }}
              </p>
              <div>
                <input
                  type="text"
                  v-model="form.vendor"
                  placeholder="Payroll or File Feed Vendor (if applicable)"
                  class="w-full p-2 bg-gray-725 rounded placeholder:text-gray-400"
                />
              </div>
              <div class="flex flex-col">
                <label for="referral">How did you hear about us?</label>
                <select
                  v-model="form.referral"
                  id="referral"
                  class="w-full p-2 bg-gray-725 rounded placeholder:text-gray-400"
                  required
                >
                  <option disabled value="">Please select one</option>
                  <option value="I'm an existing client of RMR"
                    >I'm an existing client of RMR</option
                  >
                  <option value="I've worked with RMR in the past"
                    >I've worked with RMR in the past</option
                  >
                  <option value="Colleague or Peer recommended RMR"
                    >Colleague or Peer recommended RMR</option
                  >
                  <option value="My insurance Broker/Consultant recommended RMR"
                    >My insurance Broker/Consultant recommended RMR</option
                  >
                  <option value="Web search (i.e. Google)"
                    >Web search (i.e. Google)</option
                  >
                  <option value="AssuredPartners Summit"
                    >AssuredPartners Summit</option
                  >
                  <option value="other">Other</option>
                </select>
              </div>
              <div v-if="form.referral === 'other'">
                <input
                  type="text"
                  v-model="form.otherReferral"
                  placeholder="Please specify"
                  class="w-full p-2 bg-gray-725 rounded placeholder:text-gray-400"
                />
                <p
                  v-if="errors.otherReferral"
                  class="text-red-500 text-sm mt-2 ml-4"
                >
                  * {{ errors.otherReferral }}
                </p>
              </div>

              <div class="flex justify-evenly flex-col sm:flex-row">
                <button
                  type="submit"
                  @click="submissionType = 'demo'"
                  class="bg-green-800 hover:bg-green-700 px-6 py-2 m-2 rounded text-white"
                >
                  Schedule A Demo
                </button>
                <button
                  type="submit"
                  @click="submissionType = 'proposal'"
                  class="bg-green-800 hover:bg-green-700 px-6 py-2 m-2 rounded text-white"
                >
                  Request A Proposal
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </base-view>
</template>

<script>
import BaseView from "@/components/layout/BaseView.vue";
import emailjs from "emailjs-com";

export default {
  components: {
    BaseView
  },
  data() {
    return {
      form: {
        firstName: "",
        lastName: "",
        email: "",
        companyName: "",
        headquartersState: "",
        website: "",
        employees: null,
        startDate: "",
        services: [],
        vendor: "",
        referral: "",
        otherReferral: ""
      },
      formSubmitted: false,
      submissionType: "",
      errors: {
        services: "",
        website: "",
        otherReferral: ""
      }
    };
  },
  mounted() {},
  methods: {
    sendEmail() {
      const servicesValid = this.verifyServices();
      const otherReferralValid = this.verifyOtherReferral();

      if (!servicesValid || !otherReferralValid) {
        if (!servicesValid) {
          document
            .querySelector("fieldset")
            .scrollIntoView({ behavior: "smooth" });
        } else {
          document
            .getElementById("referral")
            ?.scrollIntoView({ behavior: "smooth" });
        }
        return;
      }
      // Send email to info@rmrbenefits
      // npm install emailjs-com
      const templateParams = {
        to_email: "sales@rmrbenefits.com",
        from_name: `${this.form.firstName} ${this.form.lastName}`,
        message: `
Email: ${this.form.email}
Company: ${this.form.companyName}
State: ${this.form.headquartersState}
Website: ${this.form.website}
Employees: ${this.form.employees}
Start Date: ${this.form.startDate}
Services: ${this.form.services.join(", ")}
Vendor: ${this.form.vendor}   
${
  this.form.referral == "other"
    ? `Referral: ${this.form.otherReferral}`
    : `Referral: ${this.form.referral}`
}
`,
        action: this.submissionType.toUpperCase()
      };
      const serviceID = "rmr-talon.walz";
      const templateID = "rmr-sales";
      const userID = "m02hwyVBpO2ggwbXp"; // Found in EmailJS dashboard
      emailjs
        .send(serviceID, templateID, templateParams, userID)
        .then(() => {
          this.formSubmitted = true;
          window.scrollTo({ top: 0, behavior: "smooth" });
        })
        .catch(error => alert("Error sending email: " + error.text));
    },
    verifyServices() {
      // Check if at least one service is selected
      if (this.form.services.length === 0) {
        this.errors.services = "Please select at least one service.";
        return false;
      } else {
        this.errors.services = "";
        return true;
      }
    },
    validateWebsite() {
      const website = this.form.website.trim();
      if (!website.endsWith(".com")) {
        this.errors.website = "Website must end with .com";
      } else {
        this.errors.website = "";
      }
    },
    verifyOtherReferral() {
      if (this.form.referral === "other" && !this.form.otherReferral.trim()) {
        this.errors.otherReferral = "Please specify how you heard about us.";
        return false;
      } else {
        this.errors.otherReferral = "";
        return true;
      }
    }
  }
};
</script>
